import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  Badge,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  useDisclosure,
  useToast,
  HStack,
  VStack,
  Text,
  Spinner,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from '@chakra-ui/react';
import { FiPlus, FiEdit2, FiTrash2 } from 'react-icons/fi';
import {
  getMasterData,
  createCompanyActivity,
  updateCompanyActivity,
  deleteCompanyActivity,
} from '../../services/companyMasterDataAPI';

const CompanyActivityManager = () => {
  const [categories, setCategories] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure();
  const cancelRef = React.useRef();
  const toast = useToast();

  // Form state
  const [formData, setFormData] = useState({
    masterCategoryId: '',
    code: '',
    name: '',
    description: '',
    defaultUnit: 'No',
    order: 0,
  });

  useEffect(() => {
    loadMasterData();
  }, []);

  const loadMasterData = async () => {
    try {
      setLoading(true);
      const response = await getMasterData();
      
      if (response.success) {
        setCategories(response.data || []);
        
        // Extract all activities from categories
        const allActivities = [];
        response.data.forEach((category) => {
          if (category.masterActivities) {
            category.masterActivities.forEach((activity) => {
              allActivities.push({
                ...activity,
                categoryName: category.name,
                categoryCode: category.code,
              });
            });
          }
        });
        setActivities(allActivities);
      }
    } catch (error) {
      toast({
        title: 'Error loading master data',
        description: error.response?.data?.message || error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (activity = null) => {
    if (activity) {
      // Edit mode
      setSelectedActivity(activity);
      setFormData({
        masterCategoryId: activity.masterCategoryId,
        code: activity.code,
        name: activity.name,
        description: activity.description || '',
        defaultUnit: activity.defaultUnit || 'No',
        order: activity.order || 0,
      });
    } else {
      // Create mode
      setSelectedActivity(null);
      setFormData({
        masterCategoryId: '',
        code: '',
        name: '',
        description: '',
        defaultUnit: 'No',
        order: 0,
      });
    }
    onOpen();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (selectedActivity) {
        // Update existing activity
        await updateCompanyActivity(selectedActivity.id, formData);
        toast({
          title: 'Activity updated',
          description: 'Company activity updated successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        // Create new activity
        await createCompanyActivity(formData);
        toast({
          title: 'Activity created',
          description: 'Company activity created successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }
      
      onClose();
      loadMasterData();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleDeleteClick = (activity) => {
    setDeleteTarget(activity);
    onDeleteOpen();
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteCompanyActivity(deleteTarget.id);
      toast({
        title: 'Activity deleted',
        description: 'Company activity deleted successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      onDeleteClose();
      loadMasterData();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const isCompanyActivity = (activity) => {
    return activity.companyId !== null && activity.companyId !== undefined;
  };

  if (loading) {
    return (
      <Box textAlign="center" py={10}>
        <Spinner size="xl" />
      </Box>
    );
  }

  return (
    <Box>
      <HStack justify="space-between" mb={4}>
        <VStack align="start" spacing={0}>
          <Text fontSize="xl" fontWeight="bold" color="white">
            Activities
          </Text>
          <Text fontSize="sm" color="gray.400">
            Manage your company's custom activities
          </Text>
        </VStack>
        <Button
          leftIcon={<FiPlus />}
          colorScheme="blue"
          onClick={() => handleOpenModal()}
        >
          Add Activity
        </Button>
      </HStack>

      <Box
        bg="whiteAlpha.50"
        borderRadius="lg"
        border="1px solid"
        borderColor="whiteAlpha.200"
        overflow="hidden"
      >
        <Table variant="simple">
          <Thead bg="whiteAlpha.100">
            <Tr>
              <Th color="gray.300">Category</Th>
              <Th color="gray.300">Code</Th>
              <Th color="gray.300">Name</Th>
              <Th color="gray.300">Unit</Th>
              <Th color="gray.300">Type</Th>
              <Th color="gray.300">Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {activities.length === 0 ? (
              <Tr>
                <Td colSpan={6} textAlign="center" color="gray.400" py={8}>
                  No activities found. Click "Add Activity" to create one.
                </Td>
              </Tr>
            ) : (
              activities.map((activity) => (
                <Tr key={activity.id} _hover={{ bg: 'whiteAlpha.50' }}>
                  <Td color="white">
                    <Text fontWeight="medium">{activity.categoryName}</Text>
                    <Text fontSize="xs" color="gray.400">
                      {activity.categoryCode}
                    </Text>
                  </Td>
                  <Td color="white" fontWeight="medium">
                    {activity.code}
                  </Td>
                  <Td color="white">{activity.name}</Td>
                  <Td color="white">{activity.defaultUnit}</Td>
                  <Td>
                    {isCompanyActivity(activity) ? (
                      <Badge colorScheme="purple" fontSize="xs">
                        Company
                      </Badge>
                    ) : (
                      <Badge colorScheme="gray" fontSize="xs">
                        Global
                      </Badge>
                    )}
                  </Td>
                  <Td>
                    {isCompanyActivity(activity) ? (
                      <HStack spacing={2}>
                        <IconButton
                          aria-label="Edit activity"
                          icon={<FiEdit2 />}
                          size="sm"
                          variant="ghost"
                          colorScheme="blue"
                          onClick={() => handleOpenModal(activity)}
                        />
                        <IconButton
                          aria-label="Delete activity"
                          icon={<FiTrash2 />}
                          size="sm"
                          variant="ghost"
                          colorScheme="red"
                          onClick={() => handleDeleteClick(activity)}
                        />
                      </HStack>
                    ) : (
                      <Text fontSize="xs" color="gray.500">
                        Read-only
                      </Text>
                    )}
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </Box>

      {/* Add/Edit Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent bg="gray.800" color="white">
          <ModalHeader>
            {selectedActivity ? 'Edit Activity' : 'Add New Activity'}
          </ModalHeader>
          <ModalCloseButton />
          <form onSubmit={handleSubmit}>
            <ModalBody>
              <VStack spacing={4}>
                <FormControl isRequired>
                  <FormLabel>Category</FormLabel>
                  <Select
                    value={formData.masterCategoryId}
                    onChange={(e) =>
                      setFormData({ ...formData, masterCategoryId: e.target.value })
                    }
                    bg="whiteAlpha.100"
                    disabled={!!selectedActivity}
                  >
                    <option value="">Select a category</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.code} - {cat.name}
                      </option>
                    ))}
                  </Select>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Code</FormLabel>
                  <Input
                    value={formData.code}
                    onChange={(e) =>
                      setFormData({ ...formData, code: e.target.value.toUpperCase() })
                    }
                    placeholder="e.g., CUSTOM01"
                    bg="whiteAlpha.100"
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Name</FormLabel>
                  <Input
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    placeholder="Activity name"
                    bg="whiteAlpha.100"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Description</FormLabel>
                  <Textarea
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                    placeholder="Activity description (optional)"
                    bg="whiteAlpha.100"
                    rows={3}
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Default Unit</FormLabel>
                  <Input
                    value={formData.defaultUnit}
                    onChange={(e) =>
                      setFormData({ ...formData, defaultUnit: e.target.value })
                    }
                    placeholder="e.g., No, Sqm, Cum"
                    bg="whiteAlpha.100"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Order</FormLabel>
                  <Input
                    type="number"
                    value={formData.order}
                    onChange={(e) =>
                      setFormData({ ...formData, order: parseInt(e.target.value) || 0 })
                    }
                    bg="whiteAlpha.100"
                  />
                </FormControl>
              </VStack>
            </ModalBody>

            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onClose}>
                Cancel
              </Button>
              <Button colorScheme="blue" type="submit">
                {selectedActivity ? 'Update' : 'Create'}
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent bg="gray.800" color="white">
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Activity
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to delete "{deleteTarget?.name}"? This action cannot
              be undone.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteClose}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={handleDeleteConfirm} ml={3}>
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default CompanyActivityManager;
